/**
 * The decal atlas (ART-BIBLE §9).
 *
 * Every cabinet wears a dozen small printed things — serial plates, pocket values,
 * "AB 18" stickers, coin-slot legends. Authored naively that is a dozen unique textures,
 * a dozen materials and a dozen draw calls per machine.
 *
 * They all live on ONE 2048² sheet with ONE shared material instead, so bakeStatic() can
 * merge every decal on a cabinet into a single draw. This is the difference between an
 * arcade that runs on a laptop and one that doesn't.
 */

import {
  CanvasTexture, MeshBasicMaterial, Mesh, PlaneGeometry, SRGBColorSpace, BufferAttribute,
} from 'three';
import { css, INK, CREME, PAPIER } from './palette';
import { FONT_SIGN, FONT_MONO, printed } from './textures';

export interface DecalRect { u0: number; v0: number; u1: number; v1: number }

const SIZE = 2048;
/**
 * The gutter between patches. It was 4 px while the sheet was un-mipmapped, which was
 * exactly enough: one bilinear tap reaches half a texel. With mipmaps a distant decal is
 * sampled from mip 3–4, where one texel is 8–16 sheet texels, and 4 px of gutter lets a
 * neighbouring sticker bleed into a serial plate. 16 px keeps patches separated through
 * mip 4, which is smaller than any decal is ever drawn on screen.
 */
const PAD = 16;

class DecalAtlas {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private x = PAD;
  private y = PAD;
  private rowH = 0;
  texture: CanvasTexture;
  material: MeshBasicMaterial;
  private cache = new Map<string, DecalRect>();
  count = 0;

  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvas.width = SIZE; this.canvas.height = SIZE;
    this.ctx = this.canvas.getContext('2d')!;
    this.texture = new CanvasTexture(this.canvas);
    this.texture.colorSpace = SRGBColorSpace;
    printed(this.texture);
    this.material = new MeshBasicMaterial({
      map: this.texture, transparent: true, alphaTest: 0.04,
      depthWrite: true, toneMapped: false,
    });
    this.material.userData.shared = true;
    this.material.name = 'decals';
  }

  /** reserve a w×h pixel patch and draw into it; returns the uv rect */
  add(key: string, w: number, h: number, draw: (g: CanvasRenderingContext2D, w: number, h: number) => void): DecalRect {
    const hit = this.cache.get(key);
    if (hit) return hit;
    w = Math.ceil(w); h = Math.ceil(h);
    if (this.x + w + PAD > SIZE) { this.x = PAD; this.y += this.rowH + PAD; this.rowH = 0; }
    if (this.y + h + PAD > SIZE) {
      // atlas full: fall back to the top-left corner rather than corrupting geometry
      this.x = PAD; this.y = PAD; this.rowH = 0;
    }
    const gx = this.x, gy = this.y;
    this.ctx.save();
    this.ctx.translate(gx, gy);
    this.ctx.beginPath();
    this.ctx.rect(0, 0, w, h);
    this.ctx.clip();
    draw(this.ctx, w, h);
    this.ctx.restore();
    this.x += w + PAD;
    this.rowH = Math.max(this.rowH, h);
    // the sheet is drawn into incrementally, so every add() must rebuild the mip chain —
    // otherwise a late decal (AUSSER BETRIEB, a fresh ZINKEN stamp) is drawn at distance
    // from mips baked before it existed, i.e. from its neighbours.
    printed(this.texture);
    this.count++;
    const rect: DecalRect = { u0: gx / SIZE, v0: 1 - (gy + h) / SIZE, u1: (gx + w) / SIZE, v1: 1 - gy / SIZE };
    this.cache.set(key, rect);
    return rect;
  }

  /** a plane textured from an atlas patch — shares the atlas material, so it merges */
  plane(worldW: number, worldH: number, rect: DecalRect): Mesh {
    const geo = new PlaneGeometry(worldW, worldH);
    const uv = geo.getAttribute('uv') as BufferAttribute;
    for (let i = 0; i < uv.count; i++) {
      const u = uv.getX(i), v = uv.getY(i);
      uv.setXY(i, rect.u0 + u * (rect.u1 - rect.u0), rect.v0 + v * (rect.v1 - rect.v0));
    }
    uv.needsUpdate = true;
    const m = new Mesh(geo, this.material);
    m.userData.noInk = true;
    return m;
  }
}

export const decals = new DecalAtlas();

// ---------------------------------------------------------------- recipes

export interface DecalTextOpts {
  font?: string;
  weight?: string;
  size?: number;
  color?: number;
  bg?: number | null;
  letterSpacing?: string;
  pad?: number;
  bleed?: boolean;
  rotate?: number;
}

/** a printed legend, in one of the three sanctioned faces */
export function decalText(text: string, opts: DecalTextOpts = {}): { rect: DecalRect; aspect: number } {
  const size = opts.size ?? 48;
  const font = `${opts.weight ?? ''} ${size}px ${opts.font ?? FONT_SIGN}`.trim();
  const pad = opts.pad ?? size * 0.3;
  const probe = document.createElement('canvas').getContext('2d')!;
  probe.font = font;
  if (opts.letterSpacing) probe.letterSpacing = opts.letterSpacing;
  const w = Math.ceil(probe.measureText(text).width + pad * 2);
  const h = Math.ceil(size * 1.45 + pad * 0.5);
  const key = `t|${text}|${JSON.stringify(opts)}`;
  const rect = decals.add(key, w, h, (g, cw, ch) => {
    if (opts.bg !== null && opts.bg !== undefined) {
      g.fillStyle = css(opts.bg);
      g.fillRect(0, 0, cw, ch);
    }
    g.font = font;
    if (opts.letterSpacing) g.letterSpacing = opts.letterSpacing;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    if (opts.bleed !== false) {
      g.fillStyle = css(opts.color ?? INK, 0.3);
      g.fillText(text, cw / 2 + 1, ch / 2 + 1);
    }
    g.fillStyle = css(opts.color ?? INK);
    g.fillText(text, cw / 2, ch / 2);
  });
  return { rect, aspect: w / h };
}

/** a small printed plate at a given world width; height follows the text aspect */
export function decalPlate(text: string, worldW: number, opts: DecalTextOpts = {}): Mesh {
  const { rect, aspect } = decalText(text, opts);
  return decals.plane(worldW, worldW / aspect, rect);
}

/** the paper strip of a ticket — used by the ratchet animation */
export function decalTicket(worldW = 2.2): Mesh {
  const rect = decals.add('ticket', 128, 256, (g, w, h) => {
    g.fillStyle = css(PAPIER);
    g.fillRect(0, 0, w, h);
    g.strokeStyle = css(INK, 0.28);
    g.lineWidth = 2;
    for (let i = 1; i < 4; i++) {
      g.setLineDash([5, 5]);
      g.beginPath(); g.moveTo(0, (i * h) / 4); g.lineTo(w, (i * h) / 4); g.stroke();
    }
    g.setLineDash([]);
    g.fillStyle = css(INK, 0.75);
    g.font = `600 26px ${FONT_SIGN}`;
    g.textAlign = 'center';
    for (let i = 0; i < 4; i++) {
      g.save();
      g.translate(w / 2, (i + 0.5) * (h / 4));
      g.fillText('1', 0, 0);
      g.font = `13px ${FONT_MONO}`;
      g.fillText('TICKET', 0, 20);
      g.font = `600 26px ${FONT_SIGN}`;
      g.restore();
    }
  });
  return decals.plane(worldW, worldW * 2, rect);
}

/** the cream serial band every cabinet wears */
export function decalSerial(text: string, worldW = 3.2): Mesh {
  return decalPlate(text, worldW, { font: FONT_MONO, size: 34, color: INK, bg: CREME.base, pad: 12 });
}
